import { Helmet } from 'react-helmet-async';
import { useState, useMemo, useContext, useRef } from 'react';
// @mui
import {
  Card,
  Container,
  Stack,
  Button,
  Box,
  Modal,
  TextField,
  MenuItem
} from '@mui/material';
import MUIDataTable from "mui-datatables";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { id } from "date-fns/locale";
import { endOfDay } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import MonitoringNominalFooter from '../../../components/table/MonitoringNominalFooter';
import Iconify from '../../../components/iconify';
import ImageComponent from '../../../components/files/image';
import { TableLoader } from '../../../components/table';
import { getCurrentDate } from "../../../utils/formatDate";
// Context
import { axiosPrivate } from '../../../common/axiosPrivate';
import AuthContext from '../../../context/authContext';

import { filterMonitoringPenagihan } from '../../../utils/filterMonitoringPenagihan';

export default function MonitoringPenagihanPage() {
  const startDateRef = useRef(null);
  const endDateRef = useRef(null);
  const { user } = useContext(AuthContext);
  const [data, setData] = useState([]);
  const [transferFilter, setTransferFilter] = useState('semua');
  const [rumpunFilter, setRumpunFilter] = useState('semua');
  const visibleData = useMemo(
    () => filterMonitoringPenagihan(data, transferFilter, rumpunFilter),
    [data, transferFilter, rumpunFilter]
  );
  const [tableLoading, setTableLoading] = useState(false);
  const [modalImageOpen, setModalImageOpen] = useState(false);
  const [imageObjectKey, setImageObjectKey] = useState(null);

  const BodyComponent = useMemo(
    () => (props) => (
      <TableLoader loading={tableLoading} {...props} />
    ),
    [tableLoading]
  );

  const columns = [
    {
      name: "nomorInvoice",
      label: "Nomor Invoice",
      options: {
        filter: false,
        sort: true,
        display: false,
        customBodyRender: (value) => value || '-'
      }
    },
    {
      name: "number",
      label: "No",
      options: {
        filter: false,
        sort: false,
        customBodyRender : (value, tableMeta) => {
          const rowIndex = Number(tableMeta.rowIndex) + 1;
          return rowIndex;
        }
      },
    },
    {
      name: "kantor",
      label: "Kantor",
      options: {
       filter: true,
       sort: true,
       customBodyRender: (value) => value?.nama,
       sortCompare: order => (obj1, obj2) =>
        order === 'asc'
          ? (obj1.data?.nama || '').localeCompare(obj2.data?.nama || '')
          : (obj2.data?.nama || '').localeCompare(obj1.data?.nama || '')
      }
    },
    {
      name: "rumpun",
      label: "Rumpun",
      options: {
       filter: true,
       sort: true,
       customBodyRender: (value) => value?.nama,
       sortCompare: order => (obj1, obj2) =>
        order === 'asc'
          ? (obj1.data?.nama || '').localeCompare(obj2.data?.nama || '')
          : (obj2.data?.nama || '').localeCompare(obj1.data?.nama || '')
      }
    },
    {
      name: "anggota",
      label: "Anggota",
      options: {
       filter: true,
       sort: true,
       customBodyRender: (value) => (value?.nama ? value?.nama : '-'),
       sortCompare: order => (obj1, obj2) =>
        order === 'asc'
          ? (obj1.data?.nama || '').localeCompare(obj2.data?.nama || '')
          : (obj2.data?.nama || '').localeCompare(obj1.data?.nama || '')
      }
    },
    {
      name: "petugas",
      label: "Petugas",
      options: {
       filter: true,
       sort: true,
       customBodyRender: (value) => value?.nama,
       sortCompare: order => (obj1, obj2) =>
        order === 'asc'
          ? (obj1.data?.nama || '').localeCompare(obj2.data?.nama || '')
          : (obj2.data?.nama || '').localeCompare(obj1.data?.nama || '')      
      }
    },
    {
      name: "tanggal",
      label: "Tanggal",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => formatInTimeZone(new Date(value), 'UTC', 'dd/MM/yyyy HH:mm')
      }
    },
    {
      name: "dtd",
      label: "DTD",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => value ? 'Ya' : 'Tidak'
      }
    },
    {
      name: "tertagih",
      label: "Tertagih",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => value ? 'Ya' : 'Tidak'
      }
    },
    {
      name: "transfer",
      label: "Transfer",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => value === true ? 'Ya' : 'Tidak'
      }
    },
    {
      name: "nominal",
      label: "Nominal",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => {
          const number = Number(value);
          if (!value || Number.isNaN(number) || number === 0) return "-";
          return number.toLocaleString("id-ID"); // hasil: 1.000.000
        }
      }
    },
    {
      name: "kendala",
      label: "Kendala",
      options: {
        filter: true,
        sort: true,
        display: false
      }
    },
    {
      name: "operatorJaringan",
      label: "Operator Jaringan",
      options: {
        filter: true,
        sort: true,
        display: false
      }
    },
    {
      name: "keterangan",
      label: "Keterangan",
      options: {
        filter: true,
        sort: true,
        display: false
      }
    },
    {
      name: "koordinat",
      label: "Koordinat",
      options: {
        filter: false,
        sort: false,
        display: false,
        customBodyRender: (value) => {
          if (!value?.lat || !value?.lng) return "-";
          const { lat, lng } = value;
          const url = `https://www.google.com/maps?q=${lat},${lng}`;
          return url;
        }
      }
    },
    {
      name: "_id",
      label: "Aksi",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (id) => {
          const { koordinat, foto } = data.find((item) => item._id === id) || {};
    
          const mapLink = koordinat
            ? `https://www.google.com/maps?q=${koordinat.lat},${koordinat.lng}`
            : null;
    
          return (
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              {/* Map Button */}
              {mapLink ? (
                <a href={mapLink} target="_blank" rel="noopener noreferrer">
                  <Button variant="outlined" size="small">
                    <Iconify icon={"eva:pin-outline"} sx={{ marginRight: "4px" }} />
                    Map
                  </Button>
                </a>
              ) : (
                <Button variant="outlined" size="small" disabled>
                  <Iconify icon={"eva:pin-outline"} sx={{ marginRight: "4px" }} />
                  No Map
                </Button>
              )}
    
              {/* Foto Button */}
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleOpenModalImage(foto)}
                disabled={!foto}
              >
                <Iconify icon={"eva:image-fill"} sx={{ marginRight: "4px" }} />
                Foto
              </Button>
              <Button
                color="error"
                variant="outlined"
                size="small"
                onClick={() => handleDelete(id)}
              >
                <Iconify icon="eva:trash-2-fill" sx={{ marginRight: "4px" }} />
                Hapus
              </Button>
            </div>
          );
        },
      },
    }    
  ];
   
  const options = {
    filterType: "dropdown",
    selectableRowsHideCheckboxes: true,
    customTableBodyFooterRender: ({ data: displayData, columns: tableColumns }) => (
      <MonitoringNominalFooter displayData={displayData} rows={visibleData} columns={tableColumns} />
    ),
    downloadOptions: {
      filename: user?.kantorName ? `Pengihan-${user?.kantorName}-${getCurrentDate('-')}.csv` : `Pengihan-${getCurrentDate('-')}.csv`,
      filterOptions: {
        useDisplayedColumnsOnly: false,
        useDisplayedRowsOnly: true,
      }
    },
  };

  const handleOpenModalImage = (imageUrl) => {
    setImageObjectKey(imageUrl);
    setModalImageOpen(true);
  }

  const handleCloseModalImage = () => {
    setModalImageOpen(false);
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Apakah Anda yakin ingin menghapus data penagihan ini?')) return;

    try {
      await axiosPrivate.delete(`/penagihan/${id}`);
      setData((currentData) => currentData.filter((item) => item._id !== id));
    } catch (error) {
      console.error('Gagal menghapus data penagihan:', error);
    }
  };
  
  const handleStartDateChange = (date) => {
    startDateRef.current = date;
  };

  const handleEndDateChange = (date) => {
    endDateRef.current = date;
  };

  const filterData = () => {
    setTableLoading(true);

    const modEndDate = endOfDay(endDateRef.current);

    axiosPrivate.post(`/laporan/monitoring-penagihan`, {startDate: startDateRef.current, endDate: modEndDate})
    .then((response) => {
      const data = response?.data;
      
      setData(data);
    })
    .catch((error) => console.log(error))
    .finally(() => setTableLoading(false));
  }

  return (
    <>
      <Helmet>
        <title> Monitoring Penagihan | Sip App </title>
      </Helmet>
      <Container
        sx={{
          mt: 5
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mb: 2
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="start" sx={{ flexWrap: 'wrap', rowGap: 2 }}>
            <Box sx={{ marginRight: 2 }}>
              <LocalizationProvider adapterLocale={id} dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Dari Tanggal"
                  value={startDateRef.current}
                  onChange={handleStartDateChange}
                  slotProps={{ textField: { size: 'small' } }}
                />
              </LocalizationProvider>
            </Box>

            <Box sx={{ marginRight: 2 }}>
              <LocalizationProvider adapterLocale={id} dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Sampai Tanggal"
                  value={endDateRef.current}
                  onChange={handleEndDateChange}
                  slotProps={{ textField: { size: 'small' } }}
                />
              </LocalizationProvider>
            </Box>

            <TextField
              select
              label="Transfer"
              size="small"
              value={transferFilter}
              onChange={(event) => setTransferFilter(event.target.value)}
              sx={{ minWidth: 130, mr: 2 }}
            >
              <MenuItem value="semua">Semua</MenuItem>
              <MenuItem value="ya">Ya</MenuItem>
              <MenuItem value="tidak">Tidak</MenuItem>
            </TextField>
            <TextField
              select
              label="Produk"
              size="small"
              value={rumpunFilter}
              onChange={(event) => setRumpunFilter(event.target.value)}
              sx={{ minWidth: 150, mr: 2 }}
            >
              <MenuItem value="semua">Semua</MenuItem>
              <MenuItem value="UMP">UMP</MenuItem>
              <MenuItem value="UMIHA">UMIHA</MenuItem>
            </TextField>

            <Button variant="contained" onClick={filterData}>
              Filter
            </Button>
          </Stack>
        </Box>
        <Card>
          <MUIDataTable
            title="Monitoring Penagihan"
            data={visibleData}
            columns={columns}
            options={options}
            components={{ TableBody: BodyComponent }}
          />
        </Card>
      </Container>

      <Modal
        sx={{
          overflowY: 'scroll',
          pt: 5,
          pb: 5
        }}
        open={modalImageOpen}
        onClose={handleCloseModalImage}
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'transparent',
          boxShadow: 'none'
        }}>
          <ImageComponent
            objectKey={imageObjectKey}
            onClose={handleCloseModalImage}
          />
        </Box>
      </Modal>
    </>
  );
}
